/**
 * The export template check, in plain words (R3 T1).
 *
 * The backend measures a template (`backend/src/lib/docx-template-check.ts`);
 * these helpers turn the report into the sentences the upload dialog and the
 * export warning show, so both places say the same thing.
 *
 * Kapil's D4: a template whose export would replace part of itself is never
 * refused and never exported silently — the user is told what goes, and how
 * to fix it, and can export anyway. Two reasons, two wordings:
 * `no_placeholder` (pages after the first are replaced) and
 * `placeholder_unsafe` (the placeholder sits in a table cell, a text box or
 * beside other words, and the export takes that template text with it).
 */

import type { TemplateCheckReport, TemplateContentMeasure } from '@/lib/api'

/** The one-line how-to for adding a content placeholder. */
export const PLACEHOLDER_HOW_TO =
  'Open the template in Word, type BIDCONTEXT_CONTENT on its own line where the response should go, then save and upload it again.'

/** A template issue the export stops for, so the user can read it and choose. */
export interface TemplateIssue {
  templateId: string
  templateName: string
  report: TemplateCheckReport
}

type Problem = NonNullable<TemplateCheckReport['placeholder']['problem']>

const PROBLEM_WHERE: Record<Problem, string> = {
  in_table: 'is inside a table cell',
  in_text_box: 'is inside a text box',
  mid_sentence: 'shares its line with other words',
}

const PROBLEM_FIX: Record<Problem, string> = {
  in_table: 'Open the template in Word, move BIDCONTEXT_CONTENT onto its own line outside the table, then save and upload it again.',
  in_text_box: 'Open the template in Word, move BIDCONTEXT_CONTENT out of the text box onto its own line in the main text, then save and upload it again.',
  mid_sentence: 'Open the template in Word, put BIDCONTEXT_CONTENT on a line of its own, then save and upload it again.',
}

/** What an unsafe bookmark takes with it — the export has always removed this. */
const PROBLEM_LOSS: Record<Problem, string> = {
  in_table: 'exporting replaces the whole table it sits in',
  in_text_box: 'exporting removes the text box and the paragraph it sits in',
  mid_sentence: 'exporting removes the words beside it too',
}

function plural(n: number, one: string, many = `${one}s`): string {
  return `${n.toLocaleString('en-AU')} ${n === 1 ? one : many}`
}

function describeContents(measure: TemplateContentMeasure): string {
  const parts = [plural(measure.words, 'word')]
  if (measure.tables > 0) parts.push(plural(measure.tables, 'table'))
  if (measure.images > 0) parts.push(plural(measure.images, 'picture'))
  return parts.join(', ')
}

/** "about 3 pages (640 words, 1 table)" — the measure as an estimate, never an exact page number. */
export function describeMeasure(measure: TemplateContentMeasure): string {
  return `about ${plural(measure.estimatedPages, 'page')} (${describeContents(measure)})`
}

/** True when exporting with this template must stop and ask first. */
export function templateNeedsAttention(report: TemplateCheckReport | null | undefined): report is TemplateCheckReport {
  return !!report && report.needsAcknowledgement
}

/** The placeholder sits in the wrong place: replacing it takes template text with it. */
export function isUnsafePlaceholder(report: TemplateCheckReport): boolean {
  return report.replacedReason === 'placeholder_unsafe' && report.placeholder.problem !== null
}

function pagesSentence(report: TemplateCheckReport): string | null {
  if (!report.replaced) return null
  const amount = describeMeasure(report.replaced)
  if (report.responseGoes === 'whole_body') {
    return `Your response will replace all of this template's body text: ${amount}. Headers and footers are kept.`
  }
  return `Your response will replace everything after the first page of this template: ${amount}.`
}

/** What exporting with this template loses, in plain words. Null when nothing is at stake. */
export function templateLossSentence(report: TemplateCheckReport): string | null {
  if (!report.needsAcknowledgement) return null
  const problem = report.placeholder.problem
  if (isUnsafePlaceholder(report) && problem) {
    const where = `The content placeholder ${PROBLEM_WHERE[problem]}`
    if (report.placeholder.kind === 'bookmark') {
      const amount = report.replaced ? ` (${describeContents(report.replaced)})` : ''
      return `${where}, so ${PROBLEM_LOSS[problem]}${amount}.`
    }
    // A typed marker in the wrong place was never recognised, so the
    // no-placeholder rule applies. On the first page it stays as typed; after
    // it, it goes with the pages the response replaces — say only what's true.
    const pages = pagesSentence(report) ?? 'The response goes after the first page instead.'
    const marker = report.placeholder.typedMarker === 'kept' ? ' It stays in the file as typed.' : ''
    return `${where}, so it doesn't mark where the response goes.${marker} ${pages}`
  }
  return pagesSentence(report)
}

/** How to fix the template so the export no longer asks. */
export function templateFixHowTo(report: TemplateCheckReport): string {
  const problem = report.placeholder.problem
  return problem ? PROBLEM_FIX[problem] : PLACEHOLDER_HOW_TO
}

export interface TemplateCheckSummaryText {
  responseGoes: string
  kept: string[]
  unsupported: string[]
  notes: string[]
}

/** The whole report as short lines for the upload dialog. */
export function summariseTemplateCheck(report: TemplateCheckReport): TemplateCheckSummaryText {
  const unsafe = isUnsafePlaceholder(report)
  const responseGoes = {
    at_placeholder: unsafe
      ? 'At the content placeholder (BIDCONTEXT_CONTENT), but it sits where it takes template text with it.'
      : 'At the content placeholder (BIDCONTEXT_CONTENT).',
    after_first_break: unsafe
      ? 'After the first page, because the placeholder is in the wrong place.'
      : 'After the first page. This template has no content placeholder.',
    whole_body: unsafe
      ? 'In place of the template\'s body text, because the placeholder is in the wrong place.'
      : 'In place of the template\'s body text. This template has no content placeholder or page break.',
  }[report.responseGoes]

  const kept: string[] = []
  if (report.kept.before) {
    kept.push(report.responseGoes === 'at_placeholder' ? 'Everything before the placeholder, such as a cover page' : 'The first page, such as a cover page')
  }
  if (report.kept.after) kept.push('Everything after the placeholder, such as closing pages')
  if (report.kept.headers && report.kept.footers) kept.push('Headers and footers')
  else if (report.kept.headers) kept.push('Headers')
  else if (report.kept.footers) kept.push('Footers')

  // A misplaced placeholder is explained by the warning above the summary, so
  // it isn't repeated here.
  const unsupported: string[] = []
  if (report.multiColumnResponse) {
    unsupported.push('The response lands in a section set in columns, so it will be laid out in columns too.')
  }
  if (report.pdfSubstitutedFonts.length > 0) {
    const fonts = report.pdfSubstitutedFonts.join(' and ')
    unsupported.push(`${fonts} can't be used in PDFs, so a PDF shows a look-alike font. The Word file keeps ${report.pdfSubstitutedFonts.length === 1 ? 'it' : 'them'}.`)
  }

  const notes: string[] = []
  if (report.tableOfContents) {
    notes.push('The contents page isn\'t updated for you. After exporting, update the contents page in Word.')
  }

  return { responseGoes, kept, unsupported, notes }
}

/**
 * The template issue a refused export carries (409 TEMPLATE_CONTENT_LOSS).
 * Read from the error's shape — `code` and `details`, as `ApiRequestError`
 * has them — so this module needs no runtime import from the API client.
 */
export function templateIssueFromError(err: unknown): TemplateIssue | null {
  if (typeof err !== 'object' || err === null) return null
  const { code, details } = err as { code?: unknown; details?: unknown }
  if (code !== 'TEMPLATE_CONTENT_LOSS') return null
  if (typeof details !== 'object' || details === null) return null
  const { report, templateId, templateName } = details as Record<string, unknown>
  if (typeof report !== 'object' || report === null || typeof templateId !== 'string') return null
  return {
    templateId,
    templateName: typeof templateName === 'string' ? templateName : 'This template',
    report: report as TemplateCheckReport,
  }
}
